import { Button } from "@do/ui/components/button";
import { Tooltip, TooltipContent, TooltipTrigger } from "@do/ui/components/tooltip";
import { cn } from "@do/ui/lib/utils";
import { MicIcon, MicOffIcon, SquareIcon } from "lucide-react";
import { useTranslations } from "next-intl";
import { useCallback, useMemo } from "react";
import type { PermissionState } from "../../../hooks/use-microphone";
import { useChat } from "../../../providers/chat-provider";

type FooterInputSTTButtonProps = {
  microPhonePermission: PermissionState | null;
  requestMicrophonePermission: () => Promise<void>;
  isListening: boolean;
  handleCutMicrophone: () => void;
  handleStartMicrophone: () => void;
};

export const FooterInputSTTButton = ({
  microPhonePermission,
  requestMicrophonePermission,
  isListening,
  handleCutMicrophone,
  handleStartMicrophone,
}: FooterInputSTTButtonProps) => {
  const { disabled } = useChat();
  const t = useTranslations("do-chat-v2");

  const ariaLabel = useMemo(() => {
    if (microPhonePermission === "granted") {
      return isListening
        ? t("chat.footer.accessibility.stop-stt-aria-label")
        : t("chat.footer.accessibility.start-stt-aria-label");
    }
    if (microPhonePermission === "denied") {
      return t("chat.footer.accessibility.microphone-permission-denied-aria-label");
    }
    return t("chat.footer.accessibility.microphone-permission-prompt-aria-label");
  }, [microPhonePermission, isListening, t]);

  const handleClickSTTButton = useCallback(
    (e: React.MouseEvent<HTMLButtonElement>) => {
      e.preventDefault();

      if (microPhonePermission !== "granted") {
        void requestMicrophonePermission();
        return;
      }

      if (isListening) handleCutMicrophone();
      else handleStartMicrophone();
    },
    [
      microPhonePermission,
      requestMicrophonePermission,
      isListening,
      handleCutMicrophone,
      handleStartMicrophone,
    ]
  );

  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <Button
          id="footer-input-stt-button"
          aria-label={ariaLabel}
          aria-pressed={isListening}
          type="button"
          onClick={handleClickSTTButton}
          disabled={disabled}
          size="icon"
          className={cn(
            "absolute z-1 group/footer-input-stt-button",
            "right-(--footer-input-actions-button-offset)",
            "bottom-(--footer-input-actions-button-offset)",
            "size-(--footer-input-actions-button-size)",
            "rounded-(--footer-input-actions-button-border-radius)",
            isListening
              ? cn(
                  "bg-(--footer-input-stop-stt-button-background)/(--footer-input-stop-stt-button-background-opacity)",
                  "hover:bg-(--footer-input-stop-stt-button-hover-background)/(--footer-input-stop-stt-button-hover-background-opacity)",
                  "border-(length:--footer-input-stop-stt-button-border-width)",
                  "border-(--footer-input-stop-stt-button-border-color)/(--footer-input-stop-stt-button-border-opacity)",
                  "hover:border-(--footer-input-stop-stt-button-hover-border-color)/(--footer-input-stop-stt-button-hover-border-opacity)",
                  "[box-shadow:var(--footer-input-stop-stt-button-box-shadow)]"
                )
              : cn(
                  "bg-(--footer-input-stt-button-background)/(--footer-input-stt-button-background-opacity)",
                  "hover:bg-(--footer-input-stt-button-hover-background)/(--footer-input-stt-button-hover-background-opacity)",
                  "border-(length:--footer-input-stt-button-border-width)",
                  "border-(--footer-input-stt-button-border-color)/(--footer-input-stt-button-border-opacity)",
                  "hover:border-(--footer-input-stt-button-hover-border-color)/(--footer-input-stt-button-hover-border-opacity)",
                  "[box-shadow:var(--footer-input-stt-button-box-shadow)]"
                )
          )}
        >
          {microPhonePermission === "granted" ? (
            isListening ? (
              <SquareIcon
                id="footer-input-stt-button-icon"
                aria-hidden="true"
                className={cn(
                  "fill-current",
                  "size-(--footer-input-stop-stt-button-icon-size)",
                  "text-(--footer-input-stop-stt-button-foreground)/(--footer-input-stop-stt-button-foreground-opacity)",
                  "group-hover/footer-input-stt-button:text-(--footer-input-stop-stt-button-hover-foreground)/(--footer-input-stop-stt-button-hover-foreground-opacity)"
                )}
              />
            ) : (
              <MicIcon
                id="footer-input-stt-button-icon"
                aria-hidden="true"
                className={cn(
                  "size-(--footer-input-stt-button-icon-size)",
                  "text-(--footer-input-stt-button-foreground)/(--footer-input-stt-button-foreground-opacity)",
                  "group-hover/footer-input-stt-button:text-(--footer-input-stt-button-hover-foreground)/(--footer-input-stt-button-hover-foreground-opacity)"
                )}
              />
            )
          ) : (
            <MicOffIcon
              id="footer-input-stt-button-icon"
              aria-hidden="true"
              className={cn(
                "size-(--footer-input-stt-button-icon-size)",
                "text-(--footer-input-stt-button-foreground)/(--footer-input-stt-button-foreground-opacity)",
                "group-hover/footer-input-stt-button:text-(--footer-input-stt-button-hover-foreground)/(--footer-input-stt-button-hover-foreground-opacity)"
              )}
            />
          )}
        </Button>
      </TooltipTrigger>

      {microPhonePermission !== "granted" && (
        <TooltipContent
          side="top"
          align="end"
          arrowClassName={cn(
            "bg-(--footer-input-stt-button-tooltip-background)/(--footer-input-stt-button-tooltip-background-opacity)",
            "fill-(--footer-input-stt-button-tooltip-background)/(--footer-input-stt-button-tooltip-background-opacity)"
          )}
          className={cn(
            "max-w-64",
            "bg-(--footer-input-stt-button-tooltip-background)/(--footer-input-stt-button-tooltip-background-opacity)",
            "text-(--footer-input-stt-button-tooltip-foreground)/(--footer-input-stt-button-tooltip-foreground-opacity)"
          )}
        >
          {microPhonePermission === "denied"
            ? t("chat.footer.accessibility.microphone-permission-denied-aria-label")
            : t("chat.footer.accessibility.microphone-permission-prompt-aria-label")}
        </TooltipContent>
      )}
    </Tooltip>
  );
};
